import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { motion } from 'framer-motion';
import { EyeIcon, EyeSlashIcon, CheckIcon, XMarkIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { ErrorRecovery } from './ErrorRecovery';
import { LoadingState } from './LoadingState';

interface BasicInfoStepProps {
  onNext: () => void;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({ onNext }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const { quickSignup } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    trigger,
  } = useFormContext();

  const watchedPassword = watch('password');
  const watchedEmail = watch('email');

  // Password strength calculation
  const calculatePasswordStrength = (password: string) => {
    let score = 0;
    const feedback = [];

    if (password.length >= 8) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    if (password.length < 8) feedback.push('At least 8 characters');
    if (!/[a-z]/.test(password)) feedback.push('Lowercase letter');
    if (!/[A-Z]/.test(password)) feedback.push('Uppercase letter');
    if (!/[0-9]/.test(password)) feedback.push('Number');
    if (!/[^A-Za-z0-9]/.test(password)) feedback.push('Special character');

    return { score, feedback, maxScore: 5 };
  };

  const passwordStrength = calculatePasswordStrength(watchedPassword || '');

  // Email availability check (simulated)
  useEffect(() => {
    const checkEmailAvailability = async () => {
      if (watchedEmail && watchedEmail.length > 3 && !errors.email) {
        setIsCheckingEmail(true);
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        setEmailAvailable(true); // For demo purposes
        setIsCheckingEmail(false);
      }
    };

    const debounceTimer = setTimeout(checkEmailAvailability, 500);
    return () => clearTimeout(debounceTimer);
  }, [watchedEmail, errors.email]);

  const handleCreateAccount = async () => {
    const isValidStep = await trigger(['name', 'email', 'password', 'confirmPassword', 'terms', 'privacy']);
    if (!isValidStep) return;

    setIsCreatingAccount(true);
    setError(null);
    try {
      const formData = {
        name: watch('name'),
        email: watch('email'),
        password: watch('password'),
      };

      await quickSignup(formData);
      setUserEmail(watch('email'));
      setAccountCreated(true);
      
      // Save progress to API after quick signup (disabled for now)
      // try {
      //   await authService.updateSignupProgress(1, formData);
      //   console.log('Quick signup progress saved to API');
      // } catch (error) {
      //   console.error('Failed to save quick signup progress to API:', error);
      //   // Don't throw error - this is not critical
      // }
      
      toast.success('Account created! Check your email for verification.');
    } catch (error: any) {
      console.error('Account creation failed:', error);
      
      // Check for specific error types
      if (error.response?.status === 429 || error.message?.includes('rate limit') || error.message?.includes('Too Many Requests')) {
        setError(new Error('Email rate limit exceeded. Please wait 5-10 minutes before trying again, or use a different email address.'));
      } else if (error.message?.includes('already exists') || 
          error.message?.includes('already registered') ||
          error.message?.includes('User already registered') ||
          error.message?.includes('An account with this email already exists') ||
          (error.response?.status === 400 && error.message?.includes('email'))) {
        setError(new Error('An account with this email already exists. Please sign in instead.'));
      } else if (error.message?.includes('Email rate limit exceeded')) {
        setError(new Error('Email verification rate limit exceeded. Please wait a few minutes before trying again.'));
      } else {
        setError(error);
      }
      // Error toast is handled in AuthContext
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    setError(null);
    try {
      await handleCreateAccount();
    } finally {
      setIsRetrying(false);
    }
  };

  const handleResendEmail = async () => {
    // TODO: Implement resend email functionality
    toast.success('Verification email resent!');
  };

  const handleChangeEmail = () => {
    setAccountCreated(false);
    setUserEmail('');
  };

  // Show email verification page if account is created
  if (accountCreated) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-6"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <EnvelopeIcon className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Check your email</h3>
          <p className="text-gray-600">
            We've sent a verification link to <span className="font-medium text-gray-900">{userEmail}</span>
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-600 text-center">
            Click the verification link in your email to continue with your account setup. 
            The link will open in a new tab where you can complete your profile.
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleResendEmail}
            className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
          >
            Resend verification email
          </button>
          
          <button
            onClick={handleChangeEmail}
            className="w-full text-purple-600 py-2 px-4 rounded-lg font-medium hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
          >
            Change email address
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Let's get started</h3>
        <p className="text-sm text-gray-600">Create your account to start tracking trends</p>
      </div>

      {/* Error Recovery */}
      {error && (
        <ErrorRecovery
          error={error}
          onRetry={handleRetry}
          isRetrying={isRetrying}
        />
      )}

      {/* Loading State */}
      {isCreatingAccount && (
        <LoadingState
          isLoading={true}
          message="Creating your account..."
          showProgress={true}
          progress={50}
        />
      )}

      {/* Full Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Full Name *
        </label>
        <input
          type="text"
          id="name"
          {...register('name', { 
            required: 'Full name is required',
            minLength: { value: 2, message: 'Name must be at least 2 characters' }
          })}
          className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
            errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          placeholder="Enter your full name"
        />
        {errors.name && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 text-sm text-red-600 flex items-center"
          >
            <XMarkIcon className="h-4 w-4 mr-1" />
            {String(errors.name?.message || 'Name is required')}
          </motion.div>
        )}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address *
        </label>
        <div className="relative">
          <input
            type="email"
            id="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Please enter a valid email address',
              },
            })}
            className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
              errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            placeholder="Enter your email address"
          />
          {isCheckingEmail && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
            </div>
          )}
          {emailAvailable && !isCheckingEmail && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <CheckIcon className="h-5 w-5 text-green-500" />
            </div>
          )}
        </div>
        {errors.email && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 text-sm text-red-600 flex items-center"
          >
            <XMarkIcon className="h-4 w-4 mr-1" />
            {String(errors.email?.message || 'Email is required')}
          </motion.div>
        )}
      </div>

      {/* Password */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
          Password *
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
            })}
            className={`w-full px-3 py-2 pr-10 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
              errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            placeholder="Create a strong password"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeSlashIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
        </div>
        
        {/* Simple Password Strength Indicator */}
        {watchedPassword && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-600">Strength:</span>
              <span className={`text-xs font-medium ${
                passwordStrength.score <= 2 ? 'text-red-600' :
                passwordStrength.score <= 3 ? 'text-yellow-600' :
                passwordStrength.score <= 4 ? 'text-blue-600' : 'text-green-600'
              }`}>
                {passwordStrength.score <= 2 ? 'Weak' :
                 passwordStrength.score <= 3 ? 'Fair' :
                 passwordStrength.score <= 4 ? 'Good' : 'Strong'}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1">
              <div 
                className={`h-1 rounded-full transition-all duration-300 ${
                  passwordStrength.score <= 2 ? 'bg-red-500' :
                  passwordStrength.score <= 3 ? 'bg-yellow-500' :
                  passwordStrength.score <= 4 ? 'bg-blue-500' : 'bg-green-500'
                }`}
                style={{ width: `${(passwordStrength.score / passwordStrength.maxScore) * 100}%` }}
              ></div>
            </div>
          </div>
        )}
        
        {errors.password && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 text-sm text-red-600 flex items-center"
          >
            <XMarkIcon className="h-4 w-4 mr-1" />
            {String(errors.password?.message || 'Password is required')}
          </motion.div>
        )}
      </div>

      {/* Confirm Password */}
      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
          Confirm Password *
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            id="confirmPassword"
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (value) => {
                if (value !== watchedPassword) {
                  return 'Passwords do not match';
                }
                return true;
              }
            })}
            className={`w-full px-3 py-2 pr-10 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
              errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            placeholder="Confirm your password"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? (
              <EyeSlashIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
        </div>
        
        {errors.confirmPassword && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 text-sm text-red-600 flex items-center"
          >
            <XMarkIcon className="h-4 w-4 mr-1" />
            {String(errors.confirmPassword?.message || 'Please confirm your password')}
          </motion.div>
        )}
      </div>

      {/* Terms and Privacy */}
      <div className="space-y-3 pt-3 border-t border-gray-200">
        <div className="flex items-start">
          <input
            type="checkbox"
            id="terms"
            {...register('terms', { required: 'You must accept the terms' })}
            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded mt-0.5"
          />
          <label htmlFor="terms" className="ml-2 text-xs text-gray-700">
            I agree to the{' '}
            <button
              type="button"
              className="text-purple-600 hover:text-purple-500 underline"
              onClick={() => window.open('/terms', '_blank')}
            >
              Terms of Service
            </button>
            {' '}and{' '}
            <button
              type="button"
              className="text-purple-600 hover:text-purple-500 underline"
              onClick={() => window.open('/privacy', '_blank')}
            >
              Privacy Policy
            </button>
          </label>
        </div>

        <div className="flex items-start">
          <input
            type="checkbox"
            id="privacy"
            {...register('privacy')}
            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded mt-0.5"
          />
          <label htmlFor="privacy" className="ml-2 text-xs text-gray-700">
            I consent to receive email communications about my account and TrendBeat updates
            <span className="text-gray-500 text-xs ml-1">(optional)</span>
          </label>
        </div>
      </div>

      {/* Create Account Button */}
      <div className="pt-3">
        <button
          type="button"
          onClick={handleCreateAccount}
          disabled={!isValid || isCheckingEmail || isCreatingAccount}
          className="w-full bg-purple-600 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isCreatingAccount ? 'Creating account...' : isCheckingEmail ? 'Checking email...' : 'Create Account'}
        </button>
      </div>
    </motion.div>
  );
};

export default BasicInfoStep;

